import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { App } from "./App";
import { startChangelogPoll } from "./lib/changelog";
import { refreshAuth } from "./lib/auth";
import { runBootGuard } from "./lib/boot-guard";
import "./index.css";
// Side-effect: themeLoader injects the saved (or default) visual theme
// as a <style> tag in <head> on module load. Imported AFTER index.css so
// theme rules win on equal-specificity ties.
import "./lib/themeLoader";

// Boot guard — refuse to render the Memso app on a hostname the
// registry says belongs to a different app. Triggers a hard redirect
// to the correct counterpart on mismatch (or shows a branded error
// when no counterpart exists). Runs before any other boot work so we
// don't fire auth refresh / changelog polls against the wrong origin.
const guardOutcome = runBootGuard();
const guardAborted = guardOutcome.status === "redirected" || guardOutcome.status === "mismatch-no-counterpart";

// PWA prompt mode - the UpdateBanner handles the user-visible prompt
// when /api/version reports a different SHA than the one baked into this
// bundle. We only call the SW register here so the runtime caches are
// installed; we deliberately don't call updateSW(true) ourselves because
// the banner's applyForceRefresh() does the SW unregister + cache wipe
// + reload as one atomic flow.
if (!guardAborted) registerSW({ immediate: true });
if (!guardAborted) startChangelogPoll();

if (!guardAborted) {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

// Anti-flicker shell: the static splash markup in index.html paints the
// brand surface on first byte. We hold it visible until /auth/me resolves
// (so the user never sees a flash of the unauth landing OR an unauth
// chrome flash), then fade it out and remove it from the DOM. A 2.5s
// hard cap so a flaky network never strands the user on a spinner.
const splash = guardAborted ? null : document.getElementById("memso-splash");
if (splash) {
  let removed = false;
  const removeSplash = () => {
    if (removed) return;
    removed = true;
    splash.setAttribute("data-fading", "true");
    window.setTimeout(() => splash.parentNode?.removeChild(splash), 360);
  };
  // Wait one frame past auth-resolve so React has time to commit the
  // first real render under the splash. The fade then crossfades into
  // the resolved view (Login canvas / Onboarding / /u/<handle>) which
  // shares the deep-navy background, so there's no white flash.
  refreshAuth()
    .catch(() => undefined)
    .finally(() => requestAnimationFrame(() => requestAnimationFrame(removeSplash)));
  window.setTimeout(removeSplash, 2500);
}
